import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Card,
  CardBody,
  CardHeader,
  Center,
  Flex,
  Heading,
  SimpleGrid,
  Spinner,
  Stat,
  StatLabel,
  StatNumber,
  Text,
  useColorModeValue
} from '@chakra-ui/react';
import { useAppAuth } from './auth/AppAuthProvider';
import { Line, Doughnut } from 'react-chartjs-2';
import {
  ArcElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LineElement,
  LinearScale,
  PointElement,
  TimeScale,
  Tooltip
} from 'chart.js';
import 'chartjs-adapter-date-fns';
import { format } from 'date-fns';
import { FAST_API_BASE_STATS } from './config';
import useSkillsData from './hooks/useSkillsData';
import FoundationSkillsCandidatesChart from './components/dashboard/FoundationSkillsCandidatesChart';

ChartJS.register(
  ArcElement,
  CategoryScale,
  LinearScale,
  LineElement,
  PointElement,
  TimeScale,
  Tooltip,
  Legend
);

const skillsFormPalette = [
  'rgb(75, 192, 192)',
  'rgb(165, 145, 196)',
  'rgb(255, 99, 132)',
  'rgb(54, 162, 235)',
  'rgb(255, 206, 86)',
  'rgb(153, 102, 255)',
  'rgb(255, 159, 64)',
  'rgb(201, 203, 207)',
  'rgb(99, 255, 132)', 
  'rgb(255, 138, 101)'
];

const DashboardPage = () => {
  const { accessToken } = useAppAuth();
  const { skills } = useSkillsData(accessToken);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const mutedText = useColorModeValue('gray.600', 'gray.300');

  useEffect(() => {
    let isMounted = true;
    const controller = new AbortController();

    if (!accessToken) {
      setStats(null);
      setError('Authentication token missing.');
      setLoading(false);
      return () => {
        controller.abort();
      };
    }

    const fetchStats = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await fetch(FAST_API_BASE_STATS, {
          headers: {
            Authorization: `Bearer ${accessToken}`
          },
          signal: controller.signal
        });

        if (!response.ok) {
          throw new Error('Failed to load stats');
        }

        const data = await response.json();
        if (isMounted) {
          setStats(data);
        }
      } catch (err) {
        if (controller.signal.aborted) {
          return;
        }
        if (isMounted) {
          setStats(null);
          setError(err.message || 'Failed to load stats');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchStats();

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, [accessToken]);

  const skillNameMap = useMemo(() => {
    if (!Array.isArray(skills)) {
      return new Map();
    }

    return new Map(skills.map((skill) => [String(skill.skillid), skill.skillname]));
  }, [skills]);

  const timelineData = useMemo(() => {
    const series = Array.isArray(stats?.created_counts) ? stats.created_counts : [];
    const points = series
      .filter((entry) => entry?.date)
      .map((entry) => ({
        x: entry.date,
        y: typeof entry.count === 'number' ? entry.count : 0
      }));

    if (points.length === 0) {
      return null;
    }

    return {
      datasets: [
        {
          label: 'People created',
          data: points,
          borderColor: '#3182CE',
          backgroundColor: 'rgba(49, 130, 206, 0.15)',
          tension: 0.3,
          pointRadius: 3,
          fill: true
        }
      ]
    };
  }, [stats?.created_counts]);

  const lineOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      interaction: { intersect: false, mode: 'index' },
      scales: {
        x: {
          type: 'time',
          time: { unit: 'day', tooltipFormat: 'PP' },
          ticks: { maxRotation: 0 }
        },
        y: {
          beginAtZero: true,
          ticks: { precision: 0 }
        }
      },
      plugins: {
        legend: { display: false }
      }
    }),
    []
  );

  const skillCountsData = useMemo(() => {
    const skillCounts = Array.isArray(stats?.skill_counts) ? stats.skill_counts : [];
    const filtered = skillCounts.filter((entry) => typeof entry?.person_count === 'number' && entry.person_count > 0);

    if (filtered.length === 0) {
      return null;
    }

    const labels = filtered.map((entry) => skillNameMap.get(String(entry.skillid)) || `Skill ${entry.skillid}`);
    const counts = filtered.map((entry) => entry.person_count);
    const borderColors = counts.map((_, idx) => skillsFormPalette[idx % skillsFormPalette.length]);
    const backgroundColors = borderColors;

    return {
      labels,
      datasets: [
        {
          data: counts,
          backgroundColor: backgroundColors,
          borderColor: borderColors,
          borderWidth: 1,
          fill: false
        }
      ]
    };
  }, [skillNameMap, stats?.skill_counts]);

  const formattedLatestCreated = useMemo(() => {
    if (!stats?.latest_created) {
      return 'No people created yet';
    }

    try {
      return format(new Date(stats.latest_created), 'd MMM yyyy HH:mm');
    } catch (_) {
      return stats.latest_created;
    }
  }, [stats?.latest_created]);

  const totalPeopleDisplay = typeof stats?.total_people === 'number' ? stats.total_people.toLocaleString() : '—';

  const renderLoading = (minHeight = '200px') => (
    <Flex justify="center" align="center" minH={minHeight}>
      <Spinner color="blue.500" thickness="3px" />
    </Flex>
  );

  const renderError = () => (
    <Center py={6}>
      <Text color="red.500" textAlign="center">
        {error}
      </Text>
    </Center>
  );

  return (
    <Box py={6} px={{ base: 2, md: 0 }}>
      <Heading size="lg" mb={6} color="blue.600">
        People Dashboard
      </Heading>
      <SimpleGrid columns={{ base: 1, lg: 3 }} spacing={6} alignItems="stretch">
        <Card>
          <CardHeader pb={2}>
            <Heading size="md">Overview</Heading>
          </CardHeader>
          <CardBody>
            {loading ? (
              renderLoading('150px')
            ) : error ? (
              renderError()
            ) : (
              <SimpleGrid columns={1} spacing={4}>
                <Stat>
                  <StatLabel>Total profiles</StatLabel>
                  <StatNumber color="blue.600" fontSize={{ base: '4xl', md: '8xl' }}>
                    {totalPeopleDisplay}
                  </StatNumber>
                  <Box mt={4}>
                    <StatLabel mb={1}>Latest profile</StatLabel>
                    <Text fontSize={{ base: 'md', md: 'lg' }} fontWeight="semibold">
                      {formattedLatestCreated}
                    </Text>
                  </Box>
                </Stat>
              </SimpleGrid>
            )}
          </CardBody>
        </Card>

        <Card gridColumn={{ base: 'auto', lg: 'span 2' }} minH="360px">
          <CardHeader pb={2}>
            <Heading size="md">Candidate activity this month</Heading>
          </CardHeader>
          <CardBody>
            {loading ? (
              renderLoading('260px')
            ) : error ? (
              renderError()
            ) : timelineData ? (
              <Box minH="260px">
                <Line data={timelineData} options={lineOptions} />
              </Box>
            ) : (
              <Center minH="240px">
                <Text color={mutedText}>Not enough timeline data yet.</Text>
              </Center>
            )}
          </CardBody>
        </Card>

        <Card minH="360px">
          <CardHeader pb={2}>
            <Heading size="md">Foundational skills (all time)</Heading>
          </CardHeader>
          <CardBody>
            {loading ? (
              renderLoading('260px')
            ) : error ? (
              renderError()
            ) : skillCountsData ? (
              <Box minH="260px">
                <Doughnut data={skillCountsData} options={{ responsive: true, maintainAspectRatio: false, plugins: { legend: { position: 'bottom' } } }} />
              </Box>
            ) : (
              <Center minH="240px">
                <Text color={mutedText}>No skill count data available yet.</Text>
              </Center>
            )}
          </CardBody>
        </Card>

        <Card gridColumn={{ base: 'auto', lg: 'span 2' }} minH="360px">
          <CardHeader pb={2}>
            <Heading size="md">Foundational skills candidates this month</Heading>
          </CardHeader>
          <CardBody>
            {loading ? (
              renderLoading('260px')
            ) : error ? (
              renderError()
            ) : (
              <FoundationSkillsCandidatesChart
                entries={stats?.foundational_skill_candidates}
                emptyTextColor={mutedText}
              />
            )}
          </CardBody>
        </Card>
      </SimpleGrid>
    </Box>
  );
};

export default DashboardPage;
